import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, Building2, KanbanSquare, CheckSquare, Sparkles, Search, LogOut, Command, BookOpen, Brain, PenTool, Calendar as CalIcon, BarChart3, Dna, Dumbbell, GraduationCap, Settings as SettingsIcon, LifeBuoy, Menu, Briefcase, Radar, Library, Inbox, FolderOpen, Pill, ClipboardCheck, ShieldAlert, Wand2, Newspaper, ShieldCheck, HeartPulse } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import CommandPalette from '@/components/CommandPalette';
import AIChatDrawer from '@/components/AIChatDrawer';
import NotificationBell from '@/components/NotificationBell';
import CoachNotificationOnboarding from '@/components/CoachNotificationOnboarding';
import SetupWizard from '@/components/SetupWizard';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { api } from '@/lib/api';
import { LAYOUT, AUTH } from '@/constants/testIds';

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, testId: LAYOUT.navDashboard, end: true },
  { to: '/inbox', label: 'Inbox', icon: Inbox, testId: 'nav-inbox' },
  { to: '/coaching', label: 'Coaching Hub', icon: Dumbbell, testId: 'nav-coaching', end: true },
  { to: '/coaching/checkins', label: 'Check-Ins', icon: ClipboardCheck, testId: 'nav-checkins' },
  { to: '/coaching/data-review', label: 'Workout Data Check', icon: ShieldAlert, testId: 'nav-data-review' },
  { to: '/corporate', label: 'Corporate Clients', icon: Briefcase, testId: 'nav-corporate' },
  { to: '/rehab', label: 'Sports Rehab', icon: HeartPulse, testId: 'nav-sports-rehab' },
  { to: '/workout-library', label: 'Workout Library', icon: Library, testId: 'nav-workout-library' },
  { to: '/exercise-library', label: 'Exercise Library', icon: Dumbbell, testId: 'nav-exercise-library' },
  { to: '/resources', label: 'Resource Library', icon: FolderOpen, testId: 'nav-resources' },
  { to: '/verified-foods', label: 'Verified Foods', icon: ShieldCheck, testId: 'nav-verified-foods' },
  { to: '/calendar', label: 'Calendar', icon: CalIcon, testId: 'nav-calendar' },
  { to: '/tasks', label: 'Tasks', icon: CheckSquare, testId: LAYOUT.navTasks },
  { to: '/supplements', label: 'Supplements', icon: Pill, testId: 'nav-supplements' },
  { to: '/brain', label: 'Business Brain', icon: Brain, testId: 'nav-brain' },
  { to: '/analytics', label: 'Analytics', icon: BarChart3, testId: 'nav-analytics' },
  { to: '/content', label: 'Content Hub', icon: Newspaper, testId: 'nav-content-hub' },
  { to: '/prospect-discovery', label: 'LinkedIn Search', icon: Radar, testId: 'nav-prospect-discovery' },
  { to: '/people', label: 'Individual Prospects', icon: Users, testId: LAYOUT.navPeople },
  { to: '/companies', label: 'Business Prospects', icon: Building2, testId: LAYOUT.navCompanies },
  { to: '/pipeline', label: 'Pipeline', icon: KanbanSquare, testId: LAYOUT.navPipeline },
  { to: '/knowledge', label: 'Knowledge Base', icon: BookOpen, testId: 'nav-knowledge' },
  { to: '/dna', label: 'Business DNA', icon: Dna, testId: 'nav-dna' },
  { to: '/training', label: 'AI Training', icon: GraduationCap, testId: 'nav-training' },
  { to: '/create', label: 'AI Creator', icon: Wand2, testId: 'nav-create' },
];
const SECONDARY = [
  { to: '/settings', label: 'Settings', icon: SettingsIcon, testId: 'nav-settings' },
  { to: '/help', label: 'Help Center', icon: LifeBuoy, testId: 'nav-help' },
];

const linkClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors border-l-2 ${isActive ? 'bg-zinc-50 text-foreground border-primary font-semibold' : 'text-muted-foreground border-transparent hover:text-foreground hover:bg-zinc-50'}`;

export const UnreadBadge = ({ count, className = '' }) => {
  if (!count || count < 1) return null;
  return (
    <span data-testid="inbox-unread-badge"
      className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#FFD700] text-neutral-950 text-[10px] font-bold leading-none ${className}`}>
      {count > 99 ? '99+' : count}
    </span>
  );
};

function SidebarInner({ user, logout, navigate, onNavigate, inboxUnread }) {
  const go = (path) => { navigate(path); onNavigate?.(); };
  return (
    <>
      <div className="px-5 py-5 border-b border-border">
        <div className="flex items-center gap-2">
          <img src="/stp-icon-192.png" alt="Set The Pace Health & Fitness" className="w-8 h-8 rounded-sm object-contain" />
          <div className="leading-tight"><div className="display text-sm tracking-tight">Set The Pace</div><div className="micro-label text-[10px]">Business OS</div></div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testId} onClick={() => onNavigate?.()} className={linkClass}>
            <n.icon className="w-4 h-4" /><span className="flex-1">{n.label}</span>
            {n.to === '/inbox' && <UnreadBadge count={inboxUnread} />}
          </NavLink>
        ))}
        <button onClick={() => { onNavigate?.(); window.dispatchEvent(new CustomEvent('open-ai-assistant')); }} data-testid={LAYOUT.aiAssistantTrigger}
          className="w-full mt-4 flex items-center gap-3 px-3 py-2 text-sm rounded-sm border border-zinc-200 bg-zinc-50 hover:bg-white hover:border-zinc-300 transition-colors text-foreground">
          <Sparkles className="w-4 h-4 text-foreground" /><span className="flex-1 text-left">AI Assistant</span>
          <kbd className="hidden md:inline text-[10px] font-mono text-muted-foreground bg-white border border-zinc-200 px-1.5 py-0.5 rounded-sm">⌘/</kbd>
        </button>
        <div className="mt-4 pt-3 border-t border-border space-y-0.5">
          {SECONDARY.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testId} onClick={() => onNavigate?.()} className={linkClass}>
              <n.icon className="w-4 h-4" /><span>{n.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
      <div className="px-3 py-3 border-t border-border">
        <div className="flex items-center gap-2 px-2 py-2">
          <button onClick={() => go('/profile')} data-testid="nav-profile" className="flex items-center gap-2 flex-1 min-w-0 hover:bg-zinc-50 rounded-sm p-1 -m-1 transition-colors text-left">
            <div className="w-7 h-7 rounded-full bg-foreground text-white flex items-center justify-center text-xs font-semibold shrink-0">{(user?.name || 'U').slice(0, 1).toUpperCase()}</div>
            <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{user?.name}</div><div className="text-[11px] text-muted-foreground truncate">{user?.email}</div></div>
          </button>
          <button onClick={logout} data-testid={AUTH.logoutBtn} className="text-muted-foreground hover:text-foreground p-1 rounded-sm hover:bg-zinc-100" title="Sign out"><LogOut className="w-4 h-4" /></button>
        </div>
      </div>
    </>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inboxUnread, setInboxUnread] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    api.get('/preferences').then((r) => { if (!r.data?.onboarding_completed) setShowWizard(true); }).catch(() => {});
  }, []);

  const loadUnread = useCallback(() => {
    api.get('/coaching/messages/unread-count').then((r) => { setInboxUnread(r.data?.count || 0); }).catch(() => {});
  }, []);
  // Efficient refresh: on mount, on route change (e.g. after reading a thread), when the tab regains
  // focus, plus a gentle 30s poll. Reading a thread dispatches 'inbox-unread-changed' for instant update.
  useEffect(() => { loadUnread(); }, [loadUnread, location.pathname]);
  useEffect(() => {
    const t = setInterval(loadUnread, 30000);
    const onFocus = () => loadUnread();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('inbox-unread-changed', loadUnread);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('inbox-unread-changed', loadUnread);
    };
  }, [loadUnread]);

  const onKey = useCallback((e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setPaletteOpen(true); }
    if ((e.metaKey || e.ctrlKey) && e.key === '/') { e.preventDefault(); setAiOpen(true); }
  }, []);
  useEffect(() => { window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey); }, [onKey]);
  useEffect(() => { const h = () => setAiOpen(true); window.addEventListener('open-ai-assistant', h); return () => window.removeEventListener('open-ai-assistant', h); }, []);

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <aside data-testid={LAYOUT.sidebar} className="hidden md:flex w-60 shrink-0 border-r border-border bg-white flex-col">
        <SidebarInner user={user} logout={logout} navigate={navigate} inboxUnread={inboxUnread} />
      </aside>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col bg-white pt-[env(safe-area-inset-top)]" data-testid="mobile-sidebar">
          <SidebarInner user={user} logout={logout} navigate={navigate} onNavigate={() => setDrawerOpen(false)} inboxUnread={inboxUnread} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col min-w-0 overflow-x-clip">
        <header className="min-h-14 pt-[env(safe-area-inset-top)] border-b border-border bg-white/70 backdrop-blur-md sticky top-0 z-30 flex items-center px-4 sm:px-6 gap-3">
          <button onClick={() => setDrawerOpen(true)} data-testid="mobile-menu-trigger" className="md:hidden relative p-2 -ml-2 rounded-sm hover:bg-zinc-100 text-foreground">
            <Menu className="w-5 h-5" />
            {inboxUnread > 0 && <span data-testid="mobile-menu-unread-dot" className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-[#FFD700] ring-2 ring-white" />}
          </button>
          <button onClick={() => setPaletteOpen(true)} data-testid={LAYOUT.globalSearchTrigger}
            className="flex-1 max-w-xl flex items-center gap-3 px-3 py-1.5 border border-zinc-200 rounded-sm bg-zinc-50 text-muted-foreground hover:bg-white hover:border-zinc-300 transition-colors text-sm">
            <Search className="w-4 h-4" /><span className="flex-1 text-left truncate">Search…</span>
            <kbd className="hidden sm:inline text-[10px] font-mono bg-white border border-zinc-200 px-1.5 py-0.5 rounded-sm">⌘K</kbd>
          </button>
          <div className="hidden lg:flex items-center gap-2 text-[11px] text-muted-foreground"><Command className="w-3.5 h-3.5" /><span className="micro-label text-[10px]">Set The Pace OS</span></div>
          <NotificationBell />
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full mx-auto animate-fade-in">
          <CoachNotificationOnboarding />
          {children}
        </main>
      </div>

      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} onNavigate={(path) => { setPaletteOpen(false); navigate(path); }} />
      <AIChatDrawer open={aiOpen} setOpen={setAiOpen} />
      <SetupWizard open={showWizard} userName={user?.name} onDone={() => setShowWizard(false)} />
    </div>
  );
}
